import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import { 
  Dumbbell, 
  Smartphone, 
  CheckCircle, 
  ArrowRight, 
  Video,
  MessageCircle,
  Target,
  TrendingUp,
  ChevronDown,
  Stethoscope,
  Users,
  Clock,
  Home,
  Building
} from 'lucide-react';

const OnlineFitnessCoachingPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const howItWorks = [
    {
      step: 1,
      title: "Initial Assessment",
      description: "Complete health and fitness evaluation including goals, medical history, equipment availability, and lifestyle factors."
    },
    {
      step: 2,
      title: "Custom Program Design",
      description: "Your coach creates a personalized workout and nutrition plan tailored to your equipment, schedule, and objectives."
    },
    {
      step: 3,
      title: "Digital Delivery",
      description: "Receive your complete program through our private app with exercise demonstrations and meal plans."
    },
    {
      step: 4,
      title: "Weekly Check-ins",
      description: "Regular calls with your coach to review progress, adjust plans, and keep you accountable."
    },
    {
      step: 5,
      title: "Ongoing Support",
      description: "Message your coach anytime (except Sundays), with medical oversight from Dr. Pooja Sree."
    }
  ];

  const benefits = [
    {
      icon: <Stethoscope className="w-6 h-6" />,
      title: "Doctor-Supervised Training",
      description: "Every workout program is reviewed by our medical team to ensure safety, especially for those with health conditions."
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Train From Anywhere",
      description: "Access your workouts and nutrition plans through our private app. No gym required, though gym programs are available."
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Equipment Flexible",
      description: "Whether you have a full gym, basic home equipment, or no equipment at all, we design programs that work for you."
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Direct Coach Access",
      description: "Message your dedicated coach whenever you have questions. Get real answers, not automated responses."
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Progressive Programming",
      description: "Your program evolves as you progress. We continuously adjust difficulty, volume, and focus based on your results."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Partner Options",
      description: "Train with a spouse, sibling, or friend. Our partner plans keep you both accountable and motivated."
    }
  ];

  const trainingOptions = [
    {
      icon: <Home className="w-8 h-8" />,
      title: "Home Workouts",
      description: "Effective programs designed for home training with minimal or no equipment. Bodyweight exercises, resistance bands, and basic weights.",
      features: ["Gym membership is not mandatory", "Flexible timing", "Family-friendly scheduling"]
    },
    {
      icon: <Building className="w-8 h-8" />,
      title: "Gym Programs",
      description: "Comprehensive gym-based programs utilizing full gym equipment for maximum results. Perfect for those with gym access.",
      features: ["Machine and free weight routines", "Strength and conditioning", "Progressive overload programming"]
    },
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: "Hybrid Training",
      description: "Combine home and gym workouts based on your weekly schedule. Perfect for busy professionals with varying availability.",
      features: ["Flexible workout locations", "Consistent progress", "Adaptable to travel"]
    }
  ];

  const whoWeServe = [
    "Busy professionals with demanding schedules",
    "Parents needing flexible workout times",
    "Those preferring home workouts over gym",
    "Individuals managing health conditions",
    "Anyone seeking medical supervision for fitness",
    "Couples wanting to train together",
    "Beginners needing guided instruction",
    "Experienced fitness enthusiasts seeking structure"
  ];

  const faqs = [
    {
      question: "How does online fitness coaching work?",
      answer: "Our online fitness coaching delivers personalized workout and nutrition programs through our private app. You receive detailed exercise plans with demonstrations, nutrition guidance, and regular check-in calls with your dedicated coach. Everything is customized to your goals, equipment availability, and schedule. You can message your coach anytime (except Sundays) for guidance, and our medical team oversees all programs to ensure safety."
    },
    {
      question: "Do I need equipment for online training?",
      answer: "No, equipment is not required. We design programs based on what you have available. This could be bodyweight-only exercises, basic home equipment like resistance bands and dumbbells, or full gym access. During your initial consultation, we assess your equipment situation and create a program that maximizes your available resources."
    },
    {
      question: "How is this different from free workout videos online?",
      answer: "Free workout videos provide generic routines without personalization or progression. Our online coaching offers customized programs designed specifically for your body, goals, and limitations. You get a dedicated coach who knows your history, adjusts your program based on results, and provides accountability. Plus, our medical supervision ensures your workouts are safe for any health conditions you may have."
    },
    {
      question: "What if I have injuries or health conditions?",
      answer: "This is where our doctor-led approach truly shines. Our medical team reviews every program, considering any injuries, conditions, or medications. We modify exercises, adjust intensity, and ensure your workout program supports rather than hinders your health. Many clients with conditions like diabetes, thyroid issues, or joint problems successfully train with us because of this medical oversight."
    },
    {
      question: "How often will I communicate with my coach?",
      answer: "You will have scheduled weekly check-in calls with your coach to review progress, discuss challenges, and plan ahead. Between calls, you can message your coach anytime except Sundays. Our coaches typically respond within a few hours during working days. This combination of scheduled calls and message support ensures you never feel alone in your fitness journey."
    },
    {
      question: "Can couples or partners train together?",
      answer: "Yes, we offer partner plans where you and a spouse, sibling, or friend can train together. Both partners receive individualized programs suited to their own goals and fitness levels, while sharing the coaching relationship. Many couples find that training together improves accountability and makes the fitness journey more enjoyable."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Online Fitness Coaching | Doctor-Supervised Personal Training | TCUBE FITNESS</title>
        <meta name="description" content="Personalized online fitness coaching with medical supervision. Custom workout programs for home or gym, weekly coaching calls, and nutrition guidance. Train anywhere with expert support." />
        <meta name="keywords" content="online fitness coaching, virtual personal training, online workout program, home workout coach, online personal trainer, fitness coaching online, remote fitness training, online gym coaching, virtual fitness coach" />
        <link rel="canonical" href="https://tcubefitness.com/online-fitness-coaching" />
        <meta property="og:title" content="Online Fitness Coaching | Doctor-Supervised Personal Training | TCUBE FITNESS" />
        <meta property="og:description" content="Train from anywhere with personalized coaching and medical supervision. Custom programs for your goals and equipment." />
        <meta property="og:url" content="https://tcubefitness.com/online-fitness-coaching" />
        <meta property="og:type" content="website" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Online Fitness Coaching"
        description="Personalized online fitness coaching with medical oversight, custom workout programs for home or gym, weekly check-ins and integrated nutrition support."
        url="/online-fitness-coaching"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Online Fitness Coaching', path: '/online-fitness-coaching' },
        ]}
      />

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full text-orange-400 text-sm font-medium mb-6">
                <Dumbbell className="w-4 h-4" />
                100% Online Personal Training
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Online Fitness Coaching with Medical Supervision
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Transform your fitness from anywhere in the world. Our doctor-supervised online coaching delivers personalized workout programs, nutrition guidance, and weekly accountability calls, all through our convenient digital platform.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-orange-500/25"
                  data-testid="fitness-coaching-cta"
                >
                  Start Your Transformation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="https://wa.me/919515901777"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-800"
                >
                  Free Consultation
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-orange-400 font-bold text-lg">Doctor</p>
                  <p className="text-slate-500 text-sm">Supervised</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-emerald-400 font-bold text-lg">Weekly</p>
                  <p className="text-slate-500 text-sm">Check-ins</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-blue-400 font-bold text-lg">Custom</p>
                  <p className="text-slate-500 text-sm">Programs</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-pink-400 font-bold text-lg">Private</p>
                  <p className="text-slate-500 text-sm">App Access</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 text-center">
                Fitness Coaching <span className="text-orange-400">Reimagined</span>
              </h2>
              
              <div className="bg-slate-900/50 rounded-3xl p-8 border border-slate-800 mb-12">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  The fitness industry has evolved, and so has personal training. Online fitness coaching removes the barriers of location, rigid scheduling, and expensive gym memberships. Gym membership is not mandatory, you can also do this at home, putting expert guidance in your pocket wherever you are.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  What sets TCUBE FITNESS apart is our <Link to="/doctor-led-fitness" className="text-blue-400 hover:underline">doctor-led approach</Link>. While other online coaches focus solely on workouts, our programs are designed with medical oversight. <Link to="/#team" className="text-orange-400 hover:underline">Medical Lead Dr. Pooja Sree</Link> supervises every client's program, ensuring your fitness journey is not only effective but also safe for your individual health situation.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed">
                  Whether you are a busy professional who cannot commit to gym schedules, a parent who needs flexible workout times, or someone managing health conditions who requires supervised exercise, our online coaching delivers results on your terms.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                How Online Coaching <span className="text-orange-400">Works</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                A streamlined process designed for busy lives
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-orange-600 via-amber-600 to-orange-600 hidden md:block"></div>
                
                <div className="space-y-6">
                  {howItWorks.map((item, idx) => (
                    <div key={idx} className="flex gap-6 items-start">
                      <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-orange-600 to-amber-600 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 shadow-lg">
                        {item.step}
                      </div>
                      <div className="bg-slate-900/50 rounded-2xl p-5 border border-slate-800 flex-1">
                        <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-slate-400">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Training Options Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Training <span className="text-emerald-400">Options</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Programs designed for your environment and equipment
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {trainingOptions.map((option, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-orange-500/30 transition-all"
                >
                  <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center text-orange-400 mb-4">
                    {option.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{option.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{option.description}</p>
                  <ul className="space-y-2">
                    {option.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Why Choose <span className="text-orange-400">Our Online Coaching</span>?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                The advantages that set our program apart
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-orange-500/30 transition-all"
                >
                  <div className="w-12 h-12 bg-orange-500/10 rounded-xl flex items-center justify-center text-orange-400 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Serve Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Perfect For <span className="text-emerald-400">You If You Are</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whoWeServe.map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 bg-slate-900/50 rounded-xl p-4 border border-slate-800"
                  >
                    <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Online Coaching <span className="text-orange-400">FAQs</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Common questions about our virtual training programs
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-orange-400' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-[500px]' : 'max-h-0'}`}>
                    <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-900/50 via-amber-900/50 to-orange-900/50"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Ready to Transform Your Fitness?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Start your online coaching journey with a free consultation. No commitment required.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-100"
                >
                  Book Free Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/packages"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-white/10"
                >
                  View Packages
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Related Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/doctor-led-fitness" className="text-slate-400 hover:text-blue-400 transition-colors">Doctor-Led Fitness</Link>
                <span className="text-slate-700">|</span>
                <Link to="/medical-nutrition-therapy" className="text-slate-400 hover:text-emerald-400 transition-colors">Clinical Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/pregnancy-nutrition" className="text-slate-400 hover:text-pink-400 transition-colors">Pregnancy Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/fertility-nutrition" className="text-slate-400 hover:text-rose-400 transition-colors">Fertility Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <ProgramFramework slug="online-fitness-coaching" />
        <Footer />
      </div>
    </>
  );
};

export default OnlineFitnessCoachingPage;
